import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Flag, Trash2, Camera } from 'lucide-react';
import { useAdminLang } from '../../hooks/useAdminLang';
import { getMediaUrl } from '../../utils/mediaUrl';
import { PhotoFullscreenViewer } from '../photos/PhotoPostCard';

// Admin — Photos partagées moderation modal (opened from the dashboard stat card)
export const PhotosAdminModal = ({ onClose }) => {
  const { at } = useAdminLang();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [flagged, setFlagged] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null); // {images, index}

  const load = async () => {
    try {
      const r = await axios.get('/api/admin/photos?limit=100');
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setFlagged(r.data.flagged || 0);
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleFlag = async (photo) => {
    try {
      await axios.post(`/api/admin/photos/${photo.id}/flag`, { flag: !photo.flagged });
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const remove = async (photo) => {
    if (!window.confirm(at('photosConfirmDelete') || 'Supprimer ce post photo ?')) return;
    try {
      await axios.delete(`/api/admin/photos/${photo.id}`);
      toast.success(at('photosDeleted') || 'Supprimé');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4" onClick={onClose} data-testid="admin-photos-modal">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-pink-400" />
            <h3 className="font-bold text-white">{at('photosTitle') || 'Photos partagées'}</h3>
            <span className="text-xs text-gray-400 ml-2">{total} · {flagged} {at('photosFlagged') || 'signalées'}</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="admin-photos-close">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <p className="text-gray-400 text-center py-8">…</p>
          ) : items.length === 0 ? (
            <p className="text-gray-400 text-center py-8">{at('photosEmpty') || 'Aucune photo partagée'}</p>
          ) : (
            items.map((p) => (
              <div key={p.id} className={`bg-white/5 border rounded-2xl p-3 ${p.flagged ? 'border-red-500/50' : 'border-white/10'}`} data-testid={`admin-photo-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{p.user?.name || p.user?.username || p.user_id}</p>
                    <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {p.flagged && <span className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full">{at('photosFlaggedBadge') || 'Signalée'}</span>}
                    <button onClick={() => toggleFlag(p)} className={`p-2 rounded-full ${p.flagged ? 'bg-green-500/20 text-green-300' : 'bg-orange-500/20 text-orange-300'}`} title={p.flagged ? (at('photosUnflag') || 'Rétablir') : (at('photosFlag') || 'Signaler')} data-testid={`admin-photo-flag-${p.id}`}>
                      <Flag className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 rounded-full bg-red-500/20 text-red-300" data-testid={`admin-photo-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {p.caption && <p className="text-sm text-gray-300 mb-2 break-words">{p.caption}</p>}
                <div className="flex gap-2 overflow-x-auto">
                  {(p.image_urls || []).map((url, idx) => (
                    <img
                      key={idx}
                      src={getMediaUrl(url)}
                      alt={`admin-photo-${idx}`}
                      className="w-20 h-20 object-cover rounded-lg cursor-pointer shrink-0"
                      onClick={() => setViewer({ images: p.image_urls, index: idx })}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">❤️ {p.likes_count || 0} · 💬 {p.comments_count || 0}</p>
              </div>
            ))
          )}
        </div>
      </div>
      {viewer && <PhotoFullscreenViewer images={viewer.images} startIndex={viewer.index} onClose={() => setViewer(null)} />}
    </div>
  );
};

export default PhotosAdminModal;
